/**
 * Spring physics for the chat surface morph: a TypeScript port of the
 * prototype `Spring` (docs/concepts/chat-ux-handoff, 03-animationen.md §13).
 *
 * The surface state is centre + size (`cx, cy, w, h, r`), so a rect grows
 * around its centre. Integration is semi-implicit Euler in 240 Hz substeps;
 * use exactly this method (not an analytic solution) to match the prototype.
 * The CSS counterpart is `springLinear()`, which feeds `linear()` easings.
 * Parameter values live in `motion.ts` / `_motion.scss`, not here.
 */

import {
	SPRING_LARGE_DAMPING,
	SPRING_LARGE_RESPONSE,
	SPRING_NAV_DAMPING,
	SPRING_NAV_RESPONSE,
	SPRING_SNAP_CLOSE_H_DAMPING,
	SPRING_SNAP_CLOSE_H_RESPONSE,
	SPRING_SNAP_CLOSE_W_DAMPING,
	SPRING_SNAP_CLOSE_W_RESPONSE,
	SPRING_SNAP_OPEN_H_DAMPING,
	SPRING_SNAP_OPEN_H_RESPONSE,
	SPRING_SNAP_OPEN_W_DAMPING,
	SPRING_SNAP_OPEN_W_RESPONSE,
} from './motion';

export interface SpringParams {
	k: number;
	c: number;
}

export interface SpringConfig {
	w: SpringParams;
	h: SpringParams;
	r: SpringParams;
}

export interface SpringRect {
	x: number;
	y: number;
	w: number;
	h: number;
	r: number;
}

type Key = 'cx' | 'cy' | 'w' | 'h' | 'r';

const KEYS: readonly Key[] = [ 'cx', 'cy', 'w', 'h', 'r' ];

const SUBSTEP_HZ = 240;
/** Settled once every size is within this many px of its target ... */
const EPS_POS = 0.3;
/** ... and slower than this many px/s. */
const EPS_VEL = 3;

/**
 * iOS-style spring parameters: `response` is the undamped period in seconds,
 * `dampingRatio` is zeta. Mass is 1.
 * @param response
 * @param dampingRatio
 */
export function fromResponse(
	response: number,
	dampingRatio: number
): SpringParams {
	const omega = ( 2 * Math.PI ) / response;
	return { k: omega * omega, c: 2 * dampingRatio * omega };
}

/**
 * One parameter set for width and height, critically damped radius (no
 * overshoot), as the prototype's `cfgOf`.
 * @param params
 */
export function uniformConfig( params: SpringParams ): SpringConfig {
	return {
		w: params,
		h: params,
		r: { k: params.k, c: 2 * Math.sqrt( params.k ) },
	};
}

/** Reduced motion: overdamped (zeta about 1.98), settles without overshoot. */
export const SPRING_REDUCED: SpringParams = { k: 4000, c: 250 };

export class RectSpring {
	cur: Record< Key, number > = { cx: 0, cy: 0, w: 0, h: 0, r: 0 };
	vel: Record< Key, number > = { cx: 0, cy: 0, w: 0, h: 0, r: 0 };
	/**
	 * Seconds. While `a > 0`, `cx`/`w` are frozen (height first); while
	 * `b > 0`, `cy`/`h` are frozen (width first).
	 */
	hold = { a: 0, b: 0 };

	cfg: SpringConfig;
	reduced: boolean;

	constructor( cfg: SpringConfig, reduced = false ) {
		this.cfg = cfg;
		this.reduced = reduced;
	}

	/**
	 * Jumps to a rect and clears the velocity.
	 * @param t
	 */
	set( t: SpringRect ): void {
		this.cur = {
			cx: t.x + t.w / 2,
			cy: t.y + t.h / 2,
			w: t.w,
			h: t.h,
			r: t.r,
		};
		this.vel = { cx: 0, cy: 0, w: 0, h: 0, r: 0 };
	}

	/**
	 * Advances `dt` seconds towards `t`. Velocity survives target and config
	 * changes, so reversing mid-flight does not hitch. Returns true once
	 * settled (never while a hold is active).
	 * @param t
	 * @param dt
	 */
	step( t: SpringRect, dt: number ): boolean {
		const tg: Record< Key, number > = {
			cx: t.x + t.w / 2,
			cy: t.y + t.h / 2,
			w: t.w,
			h: t.h,
			r: t.r,
		};
		const P: Record< Key, SpringParams > = this.reduced
			? {
					cx: SPRING_REDUCED,
					w: SPRING_REDUCED,
					cy: SPRING_REDUCED,
					h: SPRING_REDUCED,
					r: SPRING_REDUCED,
			  }
			: {
					cx: this.cfg.w,
					w: this.cfg.w,
					cy: this.cfg.h,
					h: this.cfg.h,
					r: this.cfg.r,
			  };
		const n = Math.max( 1, Math.ceil( dt * SUBSTEP_HZ ) );
		const h = dt / n;
		let settled = ! ( this.hold.a > 0 || this.hold.b > 0 );
		for ( let i = 0; i < n; i++ ) {
			for ( const k of KEYS ) {
				if (
					( this.hold.a > 0 && ( k === 'cx' || k === 'w' ) ) ||
					( this.hold.b > 0 && ( k === 'cy' || k === 'h' ) )
				) {
					continue;
				}
				const a =
					P[ k ].k * ( tg[ k ] - this.cur[ k ] ) -
					P[ k ].c * this.vel[ k ];
				// Semi-implicit Euler: velocity first, then position.
				this.vel[ k ] += a * h;
				this.cur[ k ] += this.vel[ k ] * h;
			}
		}
		this.hold.a = Math.max( 0, this.hold.a - dt );
		this.hold.b = Math.max( 0, this.hold.b - dt );
		for ( const k of KEYS ) {
			if (
				Math.abs( tg[ k ] - this.cur[ k ] ) > EPS_POS ||
				Math.abs( this.vel[ k ] ) > EPS_VEL
			) {
				settled = false;
			}
		}
		return settled;
	}

	/**
	 * Writes the state to the surface element. The radius is clamped to the
	 * live shape so it never notches.
	 * @param el
	 */
	apply( el: HTMLElement ): void {
		const w = Math.max( 0, this.cur.w );
		const h = Math.max( 0, this.cur.h );
		el.style.transform = `translate3d(${ this.cur.cx - w / 2 }px,${
			this.cur.cy - h / 2
		}px,0)`;
		el.style.width = `${ w }px`;
		el.style.height = `${ h }px`;
		el.style.borderRadius = `${ Math.max(
			0,
			Math.min( this.cur.r, w / 2, h / 2 )
		) }px`;
	}
}

/**
 * Hermite smoothstep between `a` and `b`.
 * @param a
 * @param b
 * @param x
 */
export function smoothstep( a: number, b: number, x: number ): number {
	const t = Math.max( 0, Math.min( 1, ( x - a ) / ( b - a ) ) );
	return t * t * ( 3 - 2 * t );
}

/**
 * Geometric morph progress 0..1 (prototype `progOf`): mean of the width and
 * height fractions travelled from `from` to `to`.
 * @param c
 * @param c.w
 * @param c.h
 * @param from
 * @param to
 */
export function morphProgress(
	c: { w: number; h: number },
	from: SpringRect,
	to: SpringRect
): number {
	const a = ( c.w - from.w ) / Math.max( 1, to.w - from.w );
	const b = ( c.h - from.h ) / Math.max( 1, to.h - from.h );
	return Math.max( 0, Math.min( 1, ( a + b ) / 2 ) );
}

/**
 * Frame delta in seconds, clamped to 1/30 s (tab switch, jank) and divided by
 * a slow-motion factor.
 * @param now
 * @param last
 * @param slow
 */
export function frameDt( now: number, last: number, slow = 1 ): number {
	return Math.min( ( now - last ) / 1000, 1 / 30 ) / slow;
}

/**
 * CSS `linear()` easing from the analytic step response of an underdamped
 * spring (zeta < 1), sampled evenly over `durationS`. Pair the result with
 * the same duration; the last stop is a hard `1`.
 * @param response
 * @param dampingRatio
 * @param durationS
 * @param samples
 */
export function springLinear(
	response: number,
	dampingRatio: number,
	durationS: number,
	samples = 37
): string {
	const w0 = ( 2 * Math.PI ) / response;
	const z = dampingRatio;
	const root = Math.sqrt( 1 - z * z );
	const wd = w0 * root;
	const values: string[] = [];
	for ( let i = 0; i < samples; i++ ) {
		const t = ( i / ( samples - 1 ) ) * durationS;
		const x =
			1 -
			Math.exp( -z * w0 * t ) *
				( Math.cos( wd * t ) + ( z / root ) * Math.sin( wd * t ) );
		values.push( i === samples - 1 ? '1' : String( +x.toFixed( 4 ) ) );
	}
	return `linear(${ values.join( ', ' ) })`;
}

/** Large surfaces: modal, sheet, field/bubble, corner panel. */
export const SPRING_LARGE = uniformConfig(
	fromResponse( SPRING_LARGE_RESPONSE, SPRING_LARGE_DAMPING )
);

/** Nav: magnifier to search slot. */
export const SPRING_NAV = uniformConfig(
	fromResponse( SPRING_NAV_RESPONSE, SPRING_NAV_DAMPING )
);

/** Snap open: width leads (stiffer), height follows (softer). */
export const SNAP_OPEN: SpringConfig = {
	w: fromResponse( SPRING_SNAP_OPEN_W_RESPONSE, SPRING_SNAP_OPEN_W_DAMPING ),
	h: fromResponse( SPRING_SNAP_OPEN_H_RESPONSE, SPRING_SNAP_OPEN_H_DAMPING ),
	r: SPRING_LARGE.r,
};

/** Snap close: height first, width lags. */
export const SNAP_CLOSE: SpringConfig = {
	w: fromResponse(
		SPRING_SNAP_CLOSE_W_RESPONSE,
		SPRING_SNAP_CLOSE_W_DAMPING
	),
	h: fromResponse(
		SPRING_SNAP_CLOSE_H_RESPONSE,
		SPRING_SNAP_CLOSE_H_DAMPING
	),
	r: SPRING_LARGE.r,
};
